import { useState } from "react";
import { Input as TextField } from "components/ui/input";
import { useGoogleLogin } from "../GoogleLogin/useGoogleLogin";
import { GoogleLogin } from "../GoogleLogin/GoogleLogin";
import { useHistory } from "react-router-dom";
import { PAGES } from "../../../routes/PAGES";
import LangService from "../../../services/LangService";
import validation from "../validation";
import styles from "./ShortAuth.module.scss";
import Input from "../../../components/Input/Input";
import Button from "../../../components/Button/Button";
import Checkbox from "components/Checkbox/Checkbox";
import { useCommunity } from "../../../context/community";
import { useServerProperties } from "services/server-properties";
import cn from "classnames";
import { newId } from "utils";

const HACK_PHYSTECH4EVER = ['phystech4ever', 'brnetwork']

export const ShortAuth = ({
    inputRef = null,
    gtagEvent = 'welcome',
    isLanding = false,
    buttonText = '',
    nextPage = PAGES.ONE_TIME_CODE
}) => {
    const GTAG_EVENT = 'time_code_' + gtagEvent;
    const [email, setEmail] = useState("");
    const [errorText, setErrorText] = useState("");
    const [emailFieldId] = useState(newId);
    const history = useHistory();
    const { community: { domain, parentCommunityDomain } } = useCommunity();
    const langMap = LangService.map.pages.Registration;
    const checkboxId = "pp-checkbox";
    const { docs, policyConfig, consentConfig, preLoginConsents } = useServerProperties();
    const [disabled, setDisabled] = useState(true);
    const [consentCheckedMap, setConsentCheckedMap] = useState({});

    const customRoutes = [PAGES.WELCOME];
    const isCustom = (domain === 'cpoctoavito' || domain === 'cpocto') && customRoutes.includes(history.location.pathname);

    const isCheckboxShown = () => {
        // New configurable behaviour via POLICY_CONFIG
        if (policyConfig?.preLoginConsentRequired) {
            return true;
        }
        // Backward-compatible default: for phystech4ever/brnetwork, only on non-landing pages
        return HACK_PHYSTECH4EVER.includes(domain) && !isLanding;
    }

    const hasCustomPreLoginConsents = preLoginConsents.length > 0;

    const isPreLoginConsentAccepted = () => {
        if (!isCheckboxShown()) {
            return true;
        }

        if (!hasCustomPreLoginConsents) {
            return !disabled;
        }

        return preLoginConsents
            .filter((consent) => consent.required)
            .every((consent) => Boolean(consentCheckedMap[consent.id]));
    }

    const handleLoginError = (errorText) => {
        console.warn("[Login failed] res:", errorText);
        history.push(PAGES.LOGIN)
    }
    const { hasGoogle, handleGoogleSuccess, handleGoogleFailure } = useGoogleLogin({
        gtagEvent: GTAG_EVENT,
        setErrorText: handleLoginError
    })
    const authMap = LangService.map.pages.Auth;

    const handleError = () => {
        setErrorText("");
        const error = validation.email(email);
        if (error) {
            setErrorText(error);
            setTimeout(() => {
                setErrorText("");
            }, 2500);
        }

        return Boolean(error);
    };

    const loginSubmit = async () => {
        if (parentCommunityDomain) {
            const newOrigin = window.location.origin.replace(domain, parentCommunityDomain);
            const realNextPage = new URL(nextPage, newOrigin);
            realNextPage.searchParams.append('email', encodeURIComponent(email));
            window.location.href = realNextPage;
            return;
        }

        history.push(nextPage, {
            email
        });
    };

    const handleSubmit = async () => {
        if (!isPreLoginConsentAccepted() || handleError()) {
            return;
        }

        gtag("event", GTAG_EVENT, {
            method: "Email",
        });
        await loginSubmit();
    };

    const handleKeyDown = (event) => {
        if (event.keyCode === 13) {
            return handleSubmit();
        }
    };

    const handleCheckbox = async (checked) => {
        setDisabled(!checked)
    }

    const handleConsentCheckbox = (consentId, checked) => {
        setConsentCheckedMap((prev) => ({
            ...prev,
            [consentId]: checked
        }));
    }

    return (
        <>
            {hasGoogle &&
                <>
                    <GoogleLogin
                        onGoogleSuccess={handleGoogleSuccess}
                        onGoogleFailure={handleGoogleFailure}
                    />
                    <div className={styles.orContinueWith}>
                        <span>{authMap.or}</span>
                    </div>
                </>}
            <div className={cn(styles.container, {
                [styles.isLanding]: isLanding
            })}>
                {isLanding ? (
                    <Input
                        placeholder={authMap.emailPlaceholder}
                        type="email"
                        value={email}
                        onChange={setEmail}
                        onKeyDown={handleKeyDown}
                        name="email"
                        autoComplete="email"
                        spellCheck={false}
                        required
                        className={styles.input}
                        inputRef={inputRef}
                        label={errorText}
                        hasError={errorText}
                    />
                ) : (
                    <div className="flex w-full flex-col gap-1">
                        <label htmlFor={emailFieldId} className="sr-only">
                            {authMap.emailPlaceholder}
                        </label>
                        <TextField
                            id={emailFieldId}
                            ref={inputRef}
                            placeholder={authMap.emailPlaceholder}
                            type="email"
                            name="email"
                            autoComplete="email"
                            spellCheck={false}
                            required
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            onKeyDown={handleKeyDown}
                            aria-invalid={errorText ? true : undefined}
                            aria-describedby={errorText ? `${emailFieldId}-error` : undefined}
                            className={cn(
                                "h-14 rounded-lg border-border bg-card px-4 text-base text-foreground shadow-none placeholder:text-muted-foreground",
                                errorText && "border-destructive"
                            )}
                        />
                        {errorText ? (
                            <p id={`${emailFieldId}-error`} role="alert" className="text-sm text-destructive">
                                {errorText}
                            </p>
                        ) : null}
                    </div>
                )}

                {isCheckboxShown() &&
                    <div className={styles.bottomQuestion}>
                        {hasCustomPreLoginConsents ? (
                            preLoginConsents.map((consent) => {
                                const consentCheckboxId = `${checkboxId}-${consent.id}`;
                                return (
                                    <div className={styles.privacyCheckbox} key={consent.id}>
                                        <Checkbox
                                            id={consentCheckboxId}
                                            onChange={(checked) => handleConsentCheckbox(consent.id, checked)}
                                            className={styles.checkbox}
                                        />
                                        <label htmlFor={consentCheckboxId} className={styles.privacyLabel}>
                                            <span dangerouslySetInnerHTML={{ __html: consent.text }} />
                                        </label>
                                    </div>
                                );
                            })
                        ) : (
                            <div className={styles.privacyCheckbox}>
                                <Checkbox id={checkboxId} onChange={handleCheckbox} className={styles.checkbox} />
                                <label htmlFor={checkboxId} className={styles.privacyLabel}>
                                    {consentConfig?.enabled ? (
                                        <span dangerouslySetInnerHTML={{ __html: consentConfig.text }} />
                                    ) : (
                                        <>
                                            {langMap.agreePolicy[0]}
                                            <a
                                                href={docs.termsUrl}
                                                target="_blank"
                                            >
                                                {langMap.termsOfUse}
                                            </a>

                                            {langMap.agreePolicy[1]}
                                            <a
                                                href={docs.policyUrl}
                                                target="_blank"
                                            >
                                                {langMap.privacyPolicy}
                                            </a>
                                        </>
                                    )}
                                </label>
                            </div>
                        )}
                    </div>
                }
                <Button
                    wide
                    disabled={!isPreLoginConsentAccepted() || Boolean(!email) || !!errorText}
                    type="button"
                    onClick={handleSubmit}
                    style={styles.submitButton}
                    active
                >
                    {buttonText || authMap.shortAuthButton}
                </Button>
            </div>
        </>
    )
}
